// FE-SCRM-048 — Step 2: validation report. Three counters at the top,
// expandable error + warning lists with row/field/message granularity,
// and a "Fix CSV and re-upload" button that returns to step 1 without
// losing the import-type context.

import { useState } from "react";

import { Button } from "@/design-system";

import type {
  ImportSession, ValidationIssue, ValidationReport,
} from "./types";


export interface ValidationStepProps {
  session: ImportSession;
  on_continue: () => void;
  on_reupload: () => void;
}


export function ValidationStep({
  session, on_continue, on_reupload,
}: ValidationStepProps) {
  const report: ValidationReport = session.validation_report ?? {
    valid_count: 0, error_count: 0, warning_count: 0,
    errors: [], warnings: [],
  };
  const has_errors = report.error_count > 0;

  return (
    <div
      data-testid="import-step-validation"
      className="flex flex-col gap-5"
    >
      <header className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-text-primary">
          Validation report
        </h2>
        <p className="text-xs text-text-secondary">
          {session.filename
            ? `From ${session.filename}.`
            : "From your most recent upload."}
          {" "}
          Nothing has been created yet.
        </p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Counter
          testid="counter-valid"
          label="Valid rows"
          count={report.valid_count}
          tone="success"
        />
        <Counter
          testid="counter-warning"
          label="Warnings"
          count={report.warning_count}
          tone="info"
        />
        <Counter
          testid="counter-error"
          label="Errors"
          count={report.error_count}
          tone="error"
        />
      </div>

      <IssueList
        testid="import-error-list"
        title="Errors"
        tone="error"
        issues={report.errors}
        empty_copy="No errors — your CSV is ready to import."
      />
      <IssueList
        testid="import-warning-list"
        title="Warnings"
        tone="info"
        issues={report.warnings}
        empty_copy="No warnings."
      />

      <div className="flex items-center justify-end gap-2">
        {has_errors ? (
          <Button
            type="button"
            variant="primary"
            onClick={on_reupload}
          >
            Fix your CSV and re-upload
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={on_reupload}
            >
              Upload a different file
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={on_continue}
            >
              Continue
            </Button>
          </>
        )}
      </div>
    </div>
  );
}


function Counter({
  testid, label, count, tone,
}: {
  testid: string;
  label: string;
  count: number;
  tone: "success" | "info" | "error";
}) {
  const tone_classes = {
    success: "border-l-sage text-sage",
    info: "border-l-gold text-gold",
    error: "border-l-ember text-ember",
  }[tone];
  return (
    <div
      data-testid={testid}
      className={[
        "flex flex-col gap-1 rounded-md border border-border-subtle",
        "border-l-[3px] bg-raised p-3",
        tone_classes,
      ].join(" ")}
    >
      <span
        className="text-2xl font-semibold tabular-nums"
      >
        {count}
      </span>
      <span className="text-xs text-text-secondary">{label}</span>
    </div>
  );
}


function IssueList({
  testid, title, tone, issues, empty_copy,
}: {
  testid: string;
  title: string;
  tone: "error" | "info";
  issues: ValidationIssue[];
  empty_copy: string;
}) {
  const [open, set_open] = useState(true);
  if (issues.length === 0) {
    return (
      <p
        data-testid={`${testid}-empty`}
        className="text-xs text-text-tertiary"
      >
        {empty_copy}
      </p>
    );
  }
  return (
    <details
      data-testid={testid}
      open={open}
      className="rounded-md border border-border-subtle bg-raised"
    >
      <summary
        onClick={(e) => {
          e.preventDefault();
          set_open((o) => !o);
        }}
        className={[
          "cursor-pointer select-none px-3 py-2 text-sm font-medium",
          tone === "error" ? "text-ember" : "text-text-primary",
        ].join(" ")}
      >
        {title} ({issues.length})
      </summary>
      <ul className="divide-y divide-border-subtle">
        {issues.map((iss, idx) => (
          <li
            key={`${iss.row}-${iss.field}-${idx}`}
            data-testid={`${testid}-row`}
            className="flex flex-wrap gap-2 px-3 py-2 text-xs"
          >
            <span className="font-mono text-text-tertiary">
              Row {iss.row}
            </span>
            <span className="font-mono text-text-secondary">
              {iss.field}
            </span>
            <span
              className={
                tone === "error" ? "text-ember" : "text-text-primary"
              }
            >
              {iss.message}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
